// Typed client for the public signer actions (contracts/esign/public-api.schema.json), via /api/esign/<action>.

export const ESIGN_CLIENT_ACTIONS = ['resolve-link', 'send-code', 'verify-code', 'start-session', 'get-document', 'consent', 'submit', 'decline', 'download', 'verify-hash'] as const
export type EsignClientAction = typeof ESIGN_CLIENT_ACTIONS[number]

export type SignerState = 'needs_verification' | 'ready' | 'not_your_turn' | 'finished_waiting' | 'sealing' | 'completed' | 'declined' | 'voided' | 'expired' | 'unavailable' | 'invalid'
export type RecipientRole = 'signer' | 'approver' | 'viewer' | 'cc'
export type FieldType = 'signature' | 'initials' | 'date_signed' | 'name' | 'text' | 'checkbox'

export interface Branding { workspaceName: string, logoUrl: string | null, accentColor: string | null }
/**
 * Who signs in what order, as the finished signer may see it: `SigningOrder` in contracts/esign/public-api.schema.json
 * (first names as the sender entered them, never an email; at most 20). Optional: submit sends it inside the verified
 * session, and resolve-link only for a signer who has finished on a link-only link (no verification step to pass).
 * Without it the done screen keeps its generic copy.
 */
export interface SigningOrderEntry { name: string, state: 'signed' | 'next' | 'waiting', you?: boolean }
export type ResolveLinkResponse =
  | { state: 'invalid' | 'unavailable' }
  | {
    state: Exclude<SignerState, 'invalid' | 'unavailable'>
    branding: Branding
    contract: { title: string, senderName: string }
    recipient: { name: string, role: RecipientRole, maskedEmail: string | null }
    verification: 'email_code' | 'link_only' | 'in_person'
    signingOrder?: SigningOrderEntry[]
  }
export interface Disclosure { version: number, sha256: string, text: string }
export interface SessionGrant { sessionToken: string, idleExpiresAt: number, absoluteExpiresAt: number, consentRequired: boolean, disclosure: Disclosure }
export interface OwnField { id: string, type: FieldType, page: number, x: number, y: number, w: number, h: number, required: boolean, label: string | null, maxLength: number | null }
export interface OtherValue { fieldId: string, type: FieldType, page: number, x: number, y: number, w: number, h: number, value: string | boolean | null, imageUrl: string | null }
export interface GetDocumentResponse {
  pdfUrl: string
  pdfUrlExpiresAt: number
  pages: Array<{ width: number, height: number }>
  recipient: { id: string, name: string, role: RecipientRole }
  fields: OwnField[]
  otherValues: OtherValue[]
}
export type SignatureInput =
  | { kind: 'drawn', pngBase64: string }
  | { kind: 'typed', text: string, font: 'dancing_script' | 'great_vibes' | 'caveat', pngBase64: string }
export interface SubmitRequest {
  session: string
  values: Array<{ fieldId: string, value: string | boolean }>
  signature?: SignatureInput
  initials?: SignatureInput
  decision?: 'approve'
  acknowledged?: true
  intentAck: true
}

export interface SubmitResponse { state: 'finished_waiting' | 'sealing', signingOrder?: SigningOrderEntry[] }

export type VerifyHashResponse =
  | { match: false }
  | { match: true, kind: 'contract' | 'certificate', title: string, workspace_name: string, completed_at: number, signer_names: string[], signer_emails_masked: string[], seal_level: string }
  | { match: true, tombstoned: true, completed_at: number }

export class EsignApiError extends Error {
  constructor(readonly code: string, readonly status: number, readonly details?: Record<string, unknown>) {
    super(code)
    this.name = 'EsignApiError'
  }
}

export type EsignFetch = (url: string, init: { method: 'POST', headers: Record<string, string>, body: string, signal?: AbortSignal }) => Promise<{ status: number, text: () => Promise<string> }>

/**
 * Proxy budget (ESIGN_PROXY_TIMEOUT_MS, 25s) plus headroom for the network on both sides of it. A request
 * that never answers — a stalled browser→Nitro hop — would otherwise never settle and the flow's `busy`
 * flag would stay set forever (same reasoning as utils/booking/bookingTimeout.ts). The abort rejects the
 * fetch, which `call` maps to the same NETWORK EsignApiError as any other fetch failure.
 */
export const ESIGN_REQUEST_TIMEOUT_MS = 30_000

const defaultFetch: EsignFetch = (url, init) => fetch(url, { ...init, signal: AbortSignal.timeout(ESIGN_REQUEST_TIMEOUT_MS) })

export function createEsignApi(fetchImpl: EsignFetch = defaultFetch) {
  async function call<T>(action: EsignClientAction, body: object): Promise<T> {
    let res: Awaited<ReturnType<EsignFetch>>
    try {
      res = await fetchImpl(`/api/esign/${action}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    }
    catch {
      throw new EsignApiError('NETWORK', 0)
    }
    const text = await res.text()
    let json: any = null
    try { json = text ? JSON.parse(text) : null }
    catch { json = null }
    if (res.status >= 200 && res.status < 300 && json && json.ok !== false) return json as T
    const code = json && typeof json.code === 'string' ? json.code : 'INTERNAL'
    throw new EsignApiError(code, res.status, json?.details)
  }

  return {
    resolveLink: (token: string) => call<ResolveLinkResponse>('resolve-link', { token }),
    sendCode: (token: string, turnstileToken?: string) =>
      call<{ sent: true, retryAfterSeconds: number }>('send-code', turnstileToken ? { token, turnstileToken } : { token }),
    verifyCode: (token: string, code: string) => call<SessionGrant>('verify-code', { token, code }),
    startSession: (token: string) => call<SessionGrant>('start-session', { token }),
    getDocument: (session: string) => call<GetDocumentResponse>('get-document', { session }),
    consent: (session: string, disclosureVersion: number) => call<{ ok: true }>('consent', { session, disclosureVersion }),
    submit: (request: SubmitRequest) => call<SubmitResponse>('submit', JSON.parse(JSON.stringify(request))),
    decline: (session: string, reason: string) => call<{ ok: true }>('decline', { session, reason }),
    download: (session: string) => call<{ url: string, expiresAt: number }>('download', { session }),
    verifyHash: (sha256: string) => call<VerifyHashResponse>('verify-hash', { sha256 }),
  }
}

export type EsignApi = ReturnType<typeof createEsignApi>
